import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Logo from "../../assets/images/paqtory.png";
import { logoutUser, logout } from "../../redux/slices/authSlice";
import {
  IconDashboard,
  IconClipboardList,
  IconBox,
  IconUsers,
  IconTemplate,
  IconCurrencyDollar,
  IconTruck,
  IconChartBar,
  IconSettings,
  IconLogout,
  IconChevronLeft,
  IconMenu2,
  IconMessage,
  IconTicket
} from "@tabler/icons-react";

function Sidebar({ isCollapsed, toggleSidebar }) {
  // Get permissions from Redux state
  const { permissions } = useSelector((state) => state.auth);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap(); // Dispatch logoutUser thunk
      navigate('/login'); // Redirect to login page
    } catch (error) {
      dispatch(logout());
      navigate('/login');
    }
  };

  // Define navigation items with associated permissions
  const navItems = [

    { label: "Dashboard", to: "/admin", icon: <IconDashboard size={24} />, permission: null },
    { label: "Order Management", to: "/admin/orders", icon: <IconClipboardList size={24} />, permission: "edit_content" },
    { label: "Product Management", to: "/admin/products", icon: <IconBox size={24} />, permission: "edit_content" },
    { label: "User Accounts", to: "/admin/users", icon: <IconUsers size={24} />, permission: "manage_users" },
    { label: "Design and Templates", to: "/admin/templates", icon: <IconTemplate size={24} />, permission: "edit_content" },
    { label: "Payments and Transactions", to: "/admin/payments", icon: <IconCurrencyDollar size={24} />, permission: "edit_content" },
    { label: "Shipping and Delivery", to: "/admin/shipping", icon: <IconTruck size={24} />, permission: "edit_content" },
    { label: "Analytics and Reports", to: "/admin/analytics", icon: <IconChartBar size={24} />, permission: "view_reports" },
    { label: "Admin Accounts and Roles", to: "/admin/roles", icon: <IconSettings size={24} />, permission: "manage_users" },
    { label: "Contact Us Messages", to: "/admin/contact-us-messages", icon: <IconMessage size={24} />, permission: "view_reports" },
    { label: 'Coupon Management', to: '/admin/coupon', icon: <IconTicket size={24} />, permission: 'edit_content' },
  ];

  return (
    <aside
      className={`bg-[#0B0C52] text-white flex flex-col ${isCollapsed ? "w-16 px-1" : "w-64 px-2"
        } transition-all duration-300 ease-in-out h-screen fixed top-0 left-0 z-50`}
    >
      {/* Sidebar Header */}
      <div className="relative flex flex-col items-center pt-4 pb-2">
        <button
          onClick={toggleSidebar}
          className={`absolute ${isCollapsed ? "right-2 top-2" : "-right-3 top-6"
            } z-10 ${isCollapsed
              ? "text-white hover:bg-white/10 p-1 rounded-md"
              : "bg-white text-[#0B0C52] p-1 rounded-full shadow-md hover:bg-gray-100"
            } transition-all duration-200`}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? <IconMenu2 size={27} /> : <IconChevronLeft size={20} />}
        </button>

        <div className={`${isCollapsed ? "mt-8" : "mt-2"} transition-all duration-300`}>
          <div
            className={`${isCollapsed ? "w-10 h-10" : "w-16 h-16"
              } rounded-full overflow-hidden border-2 border-white hover:border-[#FFD500] transition-all duration-300`}
          >
            <img
              src={Logo}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          </div>
          {!isCollapsed && (
            <div className="text-center mt-2 text-sm font-medium">
              Admin Panel
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className={`border-b border-white/20 mx-2 ${isCollapsed ? "my-1" : "my-3"}`} />

      {/* Navigation Items */}
      <nav className="flex-1 overflow-y-auto py-2 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
        <ul className="space-y-1">
          {navItems
            .filter((item) => !item.permission || permissions.includes(item.permission)) // Filter by permissions
            .map((item) => (

              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === "/admin"}
                  className={({ isActive }) =>
                    `flex items-center rounded-md px-2 py-2 ${isCollapsed ? "justify-center" : "px-3 gap-3"
                    } ${isActive
                      ? "bg-[#FFD500] text-[#0B0C52] font-medium"
                      : "hover:bg-white/10"
                    } transition-colors duration-200`
                  }
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  {!isCollapsed && (
                    <span className="truncate transition-opacity duration-300">
                      {item.label}
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
        </ul>
      </nav>

      {/* Logout Button */}
      <div className="mt-auto pb-4">
        <div className="border-t border-white/20 mx-2 my-3" />
        <button
          onClick={handleLogout}
          className={`flex items-center w-full rounded-md px-2 py-2 ${isCollapsed ? "justify-center" : "px-3 gap-3"
            } hover:bg-white/10 transition-colors duration-200`}
        >
          <IconLogout size={20} />
          {!isCollapsed && (
            <span className="truncate transition-opacity duration-300">
              Logout
            </span>
          )}
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;